import { useId } from "react";
import { cn } from "@/lib/cn";

/**
 * The Sebas mark: a bow tie knocked out of a rounded badge — the butler nod.
 *
 * Drawn entirely in `currentColor`, so it inherits the theme with no dark-mode
 * variant. The badge is what keeps it legible at favicon size; the bow tie
 * silhouette survives down to ~16px.
 */

// The bow tie outline, authored in a 14×14 box centred on (7, 7). Shared by the
// mask here and by the static favicon in /public — keep the two in sync.
const BOW_TIE =
  "M9.4776786 9.71071 7 12.08661 4.5223214 9.71071 6.25 4.78214 4.5223214 2.4625" +
  "L9.475 2.4625 7.75 4.78214 9.4776786 9.71071 Z";

// Positions the 14×14 bow tie art inside the 32×32 badge.
const BOW_TIE_TRANSFORM = "translate(16 16) scale(1.7) translate(-7 -7)";

export type SebasMarkProps = {
  /** `solid` for app icons and dense UI; `outline` for large hero placements. */
  variant?: "solid" | "outline";
  className?: string;
  title?: string;
} & Omit<React.SVGProps<SVGSVGElement>, "title">;

export function SebasMark({ variant = "solid", className, title, ...props }: SebasMarkProps) {
  // useId() emits colons, which are awkward inside url(#…) references.
  const maskId = `sebas-mask-${useId().replace(/:/g, "")}`;

  return (
    <svg
      viewBox="0 0 32 32"
      className={cn("h-8 w-8", className)}
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : true}
      {...props}
    >
      {title ? <title>{title}</title> : null}

      {variant === "solid" ? (
        <>
          <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="32" height="32">
            <rect width="32" height="32" fill="#fff" />
            <path d={BOW_TIE} fill="#000" transform={BOW_TIE_TRANSFORM} />
          </mask>
          <rect
            x="3"
            y="3"
            width="26"
            height="26"
            rx="7.5"
            fill="currentColor"
            mask={`url(#${maskId})`}
          />
        </>
      ) : (
        <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="26" height="26" rx="7.5" strokeWidth="2.4" />
          <path d={BOW_TIE} strokeWidth="1.6" transform={BOW_TIE_TRANSFORM} />
        </g>
      )}
    </svg>
  );
}
